/// <reference path="../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../dispatcher';
import { DeleteStoredTranscriptionAndRouteToLanding } from './TranscribingActions';
import { TranscriptionRoutePathNames } from '../routes';
import { TaskListResultStatus, TaskList } from '../../backend/transcriptionManager/TaskList';
import { LogEvents } from '../../utils/LogEvents';
import { StartOverKeys } from '../common/telemetry/StartOverKeys';
import { TranscriptionSourceFileErrors, TranscriptionSourceFileError } from '../../backend/transcriptionManager/TranscriptionManagerErrors';
import { IForegroundTask } from '../../backend/interfaces/IForegroundTask';
import { HostAppSettings, HostAppSettingName } from '../../backend/office-js/HostAppSettings';
import { ITranscriptionSourceFile, ITranscriptionSourceFileProvider } from '../../backend/interfaces/ITranscriptionSourceFileProvider';
import { CreateTranscriptionSourceFileProvider } from '../../backend/factory/TranscriptionSourceFileFactory';
import { IAuthenticationProvider } from '../../backend/interfaces/IAuthenticationProvider';
import { TranscriptionManagerFactory } from '../../backend/factory/TranscriptionManagerFactory';
import { TranscribingActionTypes } from '../common/enums/transcribingEnums';
import { LoadingActionTypes, LoadingStatus } from '../common/enums/loadingEnums';
import { isEmptyString, isNullOrUndefined } from '../../utils/utils';
import { TranscriptionTelemetryErrorCodes } from '../../utils/TranscriptionTelemetryErrorCodes';
import { LogEventNames } from '../../backend/interfaces/ITelemetryLogger';
import { EMPTY_STRING, TranscribingEntryPoints, WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS } from '../../AppConstants';
import { LogError } from '../../utils/ErrorTelemetry';
import { GetNetworkConnectionMonitorInstance } from '../NetworkConnectionMonitor';
import { v4 as uuidv4 } from 'uuid';
import { SetTranscribingEntryPoints } from '../common/actions/TranscribingActions';

let loadTranscriptionSourceFileTaskList: TaskList<ITranscriptionSourceFile | null> | null = null;
let waitAndPingRequestId: string = EMPTY_STRING;

export async function LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(transcriptionSourceFileProvider: ITranscriptionSourceFileProvider, history: any, isNewTranscript: boolean, transcribingEntryPoints: TranscribingEntryPoints = TranscribingEntryPoints.DEFAULT): Promise<ITranscriptionSourceFile | null | undefined> {
  loadTranscriptionSourceFileTaskList = new TaskList<ITranscriptionSourceFile | null>([
    async () => {
      try {
        const sourceFile = await transcriptionSourceFileProvider.GetSourceFile();
        return sourceFile;
      } catch (error) {

        // if loadTranscriptionSourceFileTaskList is null, then it is an abandoned request and any result should be discarded.
        if (isNullOrUndefined(loadTranscriptionSourceFileTaskList)) {
          return null;
        }

        if (error instanceof TranscriptionSourceFileError && (error.name == TranscriptionSourceFileErrors.SourceFileNotFound || error.name == TranscriptionSourceFileErrors.InvalidRequest
          || error.name == TranscriptionSourceFileErrors.InternetConnectivityNotAvailable)) {
          // If the source file couldn't be found or onedrive request was invalid (which can happen when enterprise account is trying to access consumer account's onedrive audio file or vice versa),
          // return null so Transcribing page is loaded without an audio file.
          return null;
        }
        throw error;
      }
    }
  ]);

  const result = await loadTranscriptionSourceFileTaskList.ExecuteTaskListSequentially();
  loadTranscriptionSourceFileTaskList = null;

  if (result.status == TaskListResultStatus.Canceled)
    return null;

  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetSourceFileAction,
    payload: {
      sourceFile: result.result,
      isNewTranscript: isNewTranscript
    }
  });

  if (transcribingEntryPoints != TranscribingEntryPoints.DEFAULT) {
    SetTranscribingEntryPoints(transcribingEntryPoints);
  }
  history.replace(TranscriptionRoutePathNames.Transcribing);
  return result.result;
}

export function HandleInternetDisconnection(foregroundTask: IForegroundTask, history: any) {

  /* this check is to avoid internet updates while waitAndPingAfter defined timer is running, this
  is to avoid ping/pong between connected/disconnected state and prevent jumping to failure screen due to this,
  failure screen will be Shown , after waiting for network Recovery.*/
  if (!isEmptyString(waitAndPingRequestId)) {
    return;
  }

  LogError(LogEventNames.Warning, "Network Error during Loading. " + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);
  if (!isNullOrUndefined(foregroundTask)) {
    waitAndPingRequestId = uuidv4();
    GetNetworkConnectionMonitorInstance().waitAndPingAfter(WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS, waitAndPingRequestId).
      then((pingConnected) => {
        waitAndPingRequestId = EMPTY_STRING;
        if (history.location.pathname != TranscriptionRoutePathNames.Loading) {
          return;
        }

        // If network is reconnected/OK, then no need to post process offline
        // if network is still NOT reconnected, then post process offline.
        if (!pingConnected) {

          // Live recording or file upload task ongoing.
          if (!isNullOrUndefined(foregroundTask)) {
            foregroundTask.HandleError(TranscriptionTelemetryErrorCodes.LostInternetConnectionReason);
          }
        }
      });
  }

  // Loading task ongoing to load content for existing media.
  if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
    CancelTranscriptionSourceFileTaskListAndNavigateToTranscribing(history);
  }
}

export function CleanupRequestToNetworkConnectionMonitor() {
  if (!isEmptyString(waitAndPingRequestId)) {
    GetNetworkConnectionMonitorInstance().clearWaitAndPingRequest(waitAndPingRequestId);
    waitAndPingRequestId = EMPTY_STRING;
  }
}

// GetTranscriptionFromExistingMedia is used to get transcription data for a previously triggered transcription
export async function GetTranscriptionFromExistingMedia(authProvider: IAuthenticationProvider, history: any) {
  if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {

    const transcriptionManagerFactory = TranscriptionManagerFactory.GetInstance();
    await transcriptionManagerFactory.HandleStorageMigration();

    Dispatcher.dispatch({
      type: LoadingActionTypes.SetStatusAction,
      payload: LoadingStatus.LoadingTranscription
    });

    const transcriptionSourceFileProvider: ITranscriptionSourceFileProvider = CreateTranscriptionSourceFileProvider(transcriptionManagerFactory.GetStoredTranscription(), authProvider);
    await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(transcriptionSourceFileProvider, history, false /* isNewTranscript */);
  }
}

function CancelTranscriptionSourceFileTaskList() {
  if (loadTranscriptionSourceFileTaskList != null) {
    loadTranscriptionSourceFileTaskList.Cancel();
    loadTranscriptionSourceFileTaskList = null;
  }
}

export function CancelTranscriptionSourceFileTaskListAndNavigateToTranscribing(history: any) {
  CancelTranscriptionSourceFileTaskList();
  history.replace(TranscriptionRoutePathNames.Transcribing);
}

export async function CancelClicked(history: any, foregroundTask: IForegroundTask | null) {
  LogEvents.StartOver.Start([{ key: StartOverKeys.Reason, value: "Cancel" }]);
  if (foregroundTask != null)
    await foregroundTask.Cancel();

  CancelTranscriptionSourceFileTaskList();

  await DeleteStoredTranscriptionAndRouteToLanding(history);
  LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Cancelled by user", isFinalResponse: true });
  LogEvents.StartOver.End();
}